import { isNil } from 'lodash';

// Accounts topClass
export const ACCOUNT_LEASE_TOPCLASS = 400000;
export const ACCOUNT_CONTRACTOR_TOPCLASS = 440000;
export const ACCOUNT_RENTAL_GUARANTIES_RECEIVED_TOPCLASS = 460000;
export const ACCOUNT_RENTAL_GUARANTIES_RECEIVED_CLASS = 462000;
export const ACCOUNT_BANK_TOPCLASS = 550000;
export const ACCOUNT_AGENCY_INTERCOMPANY_TOPCLASS = 416000;
export const ACCOUNT_ACP_FUNDS_TOPCLASS = 416200;
export const ACCOUNT_PAYMENT_PROVIDER_TRANSIT_TOPCLASS = 580000;
export const ACCOUNT_PAYMENT_PROVIDER_TRANSIT_CLASS = 580100;
export const ACCOUNT_CHARGES_TOPCLASS = 600000;
export const ACCOUNT_ACP_WORKING_CAPITAL_CLASS = 610400;
export const ACCOUNT_OWNERS_TOPCLASS = 489000;
export const ACCOUNT_CHARGES_LOSS_ON_TENANT_RECEIVABLE_CLASS = 658000;
export const ACCOUNT_PAYMENT_PROVIDER_FEE_CLASS = 657000;
export const ACCOUNT_TURNOVER_TOPCLASS = 700000;
export const ACCOUNT_TURNOVER_SERVICES_TOPCLASS = 706000;
export const ACCOUNT_TURNOVER_LETTING_FEE_CLASS = 706100;
export const ACCOUNT_TURNOVER_RENT_GUARANTEE_INSURANCE_CLASS = 706200;
export const ACCOUNT_TURNOVER_MANAGEMENT_FEES_CLASS = 706300;
export const ACCOUNT_TURNOVER_SERVICE_CLEANING_CLASS = 706400;
export const ACCOUNT_TURNOVER_SALES_COMMISSION_CLASS = 706500;
export const ACCOUNT_TURNOVER_CUSTOM_LINE_CLASS = 706900;
export const ACCOUNT_TURNOVER_RENT_CLASS = 700100;
export const ACCOUNT_TURNOVER_FURNISHED_RENT_CLASS = 700200;
export const ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS = 700400;
export const ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS = 700600;
export const ACCOUNT_TURNOVER_CHARGES_ADJUSTEMENT_CLASS = 700800;
export const ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_SERVICE_CLASS = 700900;
export const ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_CHARGE_CLASS = 700950;
export const ACCOUNT_TURNOVER_TERMINATION_INDEMNITY = 700150;
export const ACCOUNT_TURNOVER_EXCEPTIONAL_REVENUE_CLASS = 758000;

export const ACCOUNT_SUSPENSE_TOPCLASS = 499000;
export const ACCOUNT_SUSPENSE_RENTAL_GUARANTIES_RECEIVED_CLASS = 499001;
export const ACCOUNT_SUSPENSE_TURNOVER_RENT_CLASS = 499002;
export const ACCOUNT_SUSPENSE_TURNOVER_FURNISHED_RENT_CLASS = 499003;
export const ACCOUNT_SUSPENSE_TURNOVER_FIXED_CHARGES_CLASS = 499004;
export const ACCOUNT_SUSPENSE_TURNOVER_PROVISIONED_CHARGES_CLASS = 499005;
export const ACCOUNT_SUSPENSE_TURNOVER_CHARGES_ADJUSTEMENT_CLASS = 499006;
export const ACCOUNT_SUSPENSE_TURNOVER_VARIOUS_OPERATIONS_SERVICE_CLASS = 499007;
export const ACCOUNT_SUSPENSE_TURNOVER_VARIOUS_OPERATIONS_CHARGE_CLASS = 499008;
export const ACCOUNT_SUSPENSE_TURNOVER_TERMINATION_INDEMNITY = 499009;
export const ACCOUNT_SUSPENSE_TURNOVER_EXCEPTIONAL_REVENUE_CLASS = 499010;

// Mirrors no turnover account on purpose: must stay out of SUSPENSE_ACCOUNT_CLASS_BY_SOURCE_CLASS
export const ACCOUNT_SUSPENSE_CHARGES_PAID_IN_THE_PAST_CLASS = 499011;

const SUSPENSE_ACCOUNT_CLASS_BY_SOURCE_CLASS: Record<number, number> = {
  [ACCOUNT_RENTAL_GUARANTIES_RECEIVED_CLASS]: ACCOUNT_SUSPENSE_RENTAL_GUARANTIES_RECEIVED_CLASS,
  [ACCOUNT_TURNOVER_RENT_CLASS]: ACCOUNT_SUSPENSE_TURNOVER_RENT_CLASS,
  [ACCOUNT_TURNOVER_FURNISHED_RENT_CLASS]: ACCOUNT_SUSPENSE_TURNOVER_FURNISHED_RENT_CLASS,
  [ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS]: ACCOUNT_SUSPENSE_TURNOVER_FIXED_CHARGES_CLASS,
  [ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS]: ACCOUNT_SUSPENSE_TURNOVER_PROVISIONED_CHARGES_CLASS,
  [ACCOUNT_TURNOVER_CHARGES_ADJUSTEMENT_CLASS]: ACCOUNT_SUSPENSE_TURNOVER_CHARGES_ADJUSTEMENT_CLASS,
  [ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_SERVICE_CLASS]: ACCOUNT_SUSPENSE_TURNOVER_VARIOUS_OPERATIONS_SERVICE_CLASS,
  [ACCOUNT_TURNOVER_VARIOUS_OPERATIONS_CHARGE_CLASS]: ACCOUNT_SUSPENSE_TURNOVER_VARIOUS_OPERATIONS_CHARGE_CLASS,
  [ACCOUNT_TURNOVER_TERMINATION_INDEMNITY]: ACCOUNT_SUSPENSE_TURNOVER_TERMINATION_INDEMNITY,
  [ACCOUNT_TURNOVER_EXCEPTIONAL_REVENUE_CLASS]: ACCOUNT_SUSPENSE_TURNOVER_EXCEPTIONAL_REVENUE_CLASS,
};

const MIRRORED_SUSPENSE_ACCOUNT_CLASSES = new Set(Object.values(SUSPENSE_ACCOUNT_CLASS_BY_SOURCE_CLASS));

const SOURCE_CLASS_BY_SUSPENSE_ACCOUNT_CLASS = Object.entries(SUSPENSE_ACCOUNT_CLASS_BY_SOURCE_CLASS).reduce<
  Record<number, number>
>((accumulator, [sourceClass, suspenseClass]) => {
  accumulator[suspenseClass] = Number(sourceClass);
  return accumulator;
}, {});

export const getSuspenseAccountClassFor = (accountClass: number): number | undefined =>
  SUSPENSE_ACCOUNT_CLASS_BY_SOURCE_CLASS[accountClass];

export const getMirroredAccountClassFor = (suspenseAccountClass: number): number | undefined =>
  SOURCE_CLASS_BY_SUSPENSE_ACCOUNT_CLASS[suspenseAccountClass];

export const isMirroredSuspenseAccountClass = (accountClass: number): boolean =>
  MIRRORED_SUSPENSE_ACCOUNT_CLASSES.has(accountClass);

/** The whole migration footprint: the mirror classes and the carry-forward settlement class, never exported. */
export const isMigrationSuspenseAccountClass = (accountClass: number): boolean =>
  isMirroredSuspenseAccountClass(accountClass) || accountClass === ACCOUNT_SUSPENSE_CHARGES_PAID_IN_THE_PAST_CLASS;

export const withMirroredSuspenseAccountClasses = (accountClasses: number[]): number[] =>
  accountClasses.reduce<number[]>((allAccountClasses, accountClass) => {
    allAccountClasses.push(accountClass);
    const suspenseAccountClass = getSuspenseAccountClassFor(accountClass);
    if (suspenseAccountClass !== undefined) {
      allAccountClasses.push(suspenseAccountClass);
    }
    return allAccountClasses;
  }, []);

export const isAccountClassOrItsMirror = (
  accountClass: number | null | undefined,
  sourceAccountClass: number
): boolean => {
  if (isNil(accountClass)) {
    return false;
  }

  return accountClass === sourceAccountClass || accountClass === getSuspenseAccountClassFor(sourceAccountClass);
};

const ACCOUNT_CLASS_GROUP_DIVIDER = 100000;

const getAccountClassGroup = (accountNumber: number): number => Math.floor(accountNumber / ACCOUNT_CLASS_GROUP_DIVIDER);

export const isSameAccountClassGroup = (firstAccountNumber: number, secondAccountNumber: number): boolean =>
  getAccountClassGroup(firstAccountNumber) === getAccountClassGroup(secondAccountNumber);
